"use client";

import type { ImageRef } from "@/lib/content/schemas/primitives";

import { trustedByLogos } from "./data";

import NextImage from "next/image";
import Marquee from "@/components/shared/carousel/Marquee";

type TrustedByLogosProps = {
  title?: string;
  logos?: { logo: ImageRef; link?: string }[];
};

export default function TrustedByLogos({ title, logos = trustedByLogos }: TrustedByLogosProps) {
  if (!logos || logos.length === 0) return null;

  // An even number of copies keeps the -50% marquee loop seamless; at least
  // 16 items per half so the strip fills wide viewports without a gap.
  const copies = 2 * Math.ceil(16 / logos.length);
  const extendedLogos = Array.from({ length: copies }, () => logos).flat();

  return (
    <section className="col-span-full py-10 text-center">
      {title && (
        <p className="text-center text-lg/8 font-semibold text-white mb-8">{title}</p>
      )}
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-[100px] bg-linear-to-r from-[#101010] via-[#101010]/50 to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-[100px] bg-gradient-to-l from-[#101010] via-[#101010]/50 to-transparent" />

        <Marquee speed={30} stripClassName="items-center">
          {extendedLogos.map((item, index) => {
            const img = (
              <NextImage
                src={item.logo.src ?? ""}
                alt={item.logo.alt}
                width={120}
                height={24}
                className="h-5 md:h-6 w-auto object-contain brightness-0 invert"
                loading="eager"
              />
            );

            return (
              <div
                key={`${item.logo.alt}-${index}`}
                className="mx-8 shrink-0"
                style={{ flex: "0 0 auto" }}
              >
                {item.link ? (
                  <a href={item.link} target="_blank" rel="noopener noreferrer">
                    {img}
                  </a>
                ) : (
                  img
                )}
              </div>
            );
          })}
        </Marquee>
      </div>
    </section>
  );
}
